import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../app/store';
import { fetchRequisitionDetails, RequisitionDetail } from './requisitionSlice';
import { formatCurrency, formatDate } from '../../utils/formatUtils';
import { nl2br } from '../../utils/nl2br';

interface QuickViewModalProps {
  requisitionId: number | null;
  onClose: () => void;
}

const QuickViewModal: React.FC<QuickViewModalProps> = ({ requisitionId, onClose }) => {
  const dispatch: AppDispatch = useDispatch();
  const [requisition, setRequisition] = useState<RequisitionDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (requisitionId) {
      setLoading(true);
      dispatch(fetchRequisitionDetails(requisitionId.toString()))
        .unwrap()
        .then((data) => {
          setRequisition(data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [requisitionId, dispatch]);

  if (!requisitionId) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            ATR ID: {requisitionId} - Assets & Descriptions
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : requisition ? (
            <div className="space-y-6">
              {/* Quotation Section */}
              {requisition.quotation && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Quotation</h3>
                  <div className="p-3 border border-green-200 dark:border-green-700 rounded-md bg-green-50 dark:bg-green-900/70 shadow-sm">
                    <div className="space-y-2">
                      {/* Quotation Number */}
                      {requisition.quotation.quotation_number && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Quotation #:</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {requisition.quotation.quotation_number}
                          </span>
                        </div>
                      )}

                      {/* Date */}
                      {requisition.quotation.date && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Date:</span>
                          <span className="text-sm text-gray-900 dark:text-gray-100">
                            {formatDate(requisition.quotation.date)}
                          </span>
                        </div>
                      )}

                      {/* Amount */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Amount:</span>
                        <span className="text-lg font-bold text-green-600 dark:text-green-400">
                          {formatCurrency(requisition.quotation.amount)}
                        </span>
                      </div>

                      {/* VAT Status */}
                      {requisition.quotation.vat !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">VAT:</span>
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {requisition.quotation.vat === 1 ? 'Inclusive' : 'Exclusive'}
                          </span>
                        </div>
                      )}

                      {/* Quote attachment link if available */}
                      {(requisition.quotation as any)?.attachment && (
                        <div className="flex justify-end pt-2 border-t border-green-100 dark:border-green-800">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}/quote-file/${(requisition.quotation as any).attachment}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View Quote
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Items to Repair / Request Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Items to Repair / Request</h3>
                {(requisition.equipmentItems && requisition.equipmentItems.length > 0) || (requisition.assetDescriptionPairs && requisition.assetDescriptionPairs.length > 0) || (requisition.assets && requisition.assets.length > 0) || (requisition.equipmentRequests && requisition.equipmentRequests.length > 0) ? (
                  <div className="space-y-3">
                    {(() => {
                      const items = [];

                      // First, use processed equipment items from Laravel (matches Livewire exactly)
                      if (requisition.equipmentItems && requisition.equipmentItems.length > 0) {
                        requisition.equipmentItems.forEach((item, index) => {
                          items.push(
                            <div
                              key={`processed-eq-${index}`}
                              className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  <strong>{item.asset}</strong><br />
                                  <small>Category: {item.equipment.category} Subcategory: {item.equipment.subcategory}</small>
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                  E
                                </span>
                              </div>
                              <div className="mt-1 border-t border-amber-100 dark:border-amber-800 pt-1">
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  <span className="font-medium">Serial Number:</span> {item.equipment.serial_number}
                                </p>
                                {item.descriptions && item.descriptions.length > 0 && (
                                  <div className="mt-1">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      <span className="font-medium">Repair Description:</span>
                                    </p>
                                    <ul className="list-disc list-inside ml-2 text-xs text-gray-600 dark:text-gray-400">
                                      {item.descriptions.map((desc, dIndex) => 
                                        desc ? <li key={dIndex}>{nl2br(desc)}</li> : null
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                      }

                      // Second, use processed asset description pairs from Laravel
                      if (requisition.assetDescriptionPairs && requisition.assetDescriptionPairs.length > 0) {
                        requisition.assetDescriptionPairs.forEach((pair, index) => {
                          items.push(
                            <div
                              key={`processed-asset-${index}`}
                              className="p-2.5 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-100 dark:bg-indigo-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  Asset: {pair.asset}
                                </p>
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200">
                                  A
                                </span>
                              </div>
                              <div className="mt-1 border-t border-indigo-100 dark:border-indigo-800 pt-1">
                                {pair.descriptions && pair.descriptions.length > 0 ? (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Description:</span> {nl2br(pair.descriptions.join('\n'))}
                                  </p>
                                ) : (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Description:</span> <em>No description</em>
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        });
                      }

                      // Fallback to legacy data only if no processed data is available
                      if (items.length === 0) {
                        // Use the same deduplication logic as RequisitionDetailPage
                        const displayedDescriptions = new Set<string>();

                        // Render equipment requests
                        const equipmentItems = requisition.equipmentRequests?.map((item, index) => (
                          <div
                            key={`eq-${item.id || index}`}
                            className="p-2.5 border border-amber-200 dark:border-amber-700 rounded-md bg-amber-100 dark:bg-amber-900/70 shadow-sm"
                          >
                            <div className="flex items-start justify-between">
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                {item.equipment?.description || 'Equipment Item'}
                                {!!item.equipment?.serial_number && (
                                  <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-400">
                                    (S/N: {item.equipment.serial_number})
                                  </span>
                                )}
                              </p>
                              <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-200">
                                E
                              </span>
                            </div>
                            {!!item.notes && (
                              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 border-t border-amber-100 dark:border-amber-800 pt-1">
                                <span className="font-medium">Notes:</span> {item.notes}
                              </p>
                            )}
                          </div>
                        )) || [];

                        // Render asset items with their descriptions
                        const assetItems = requisition.assets?.map((item, index) => {
                          // Add any available descriptions to our tracking set
                          if (item.descriptions) {
                            item.descriptions.forEach(desc => {
                              if (desc.description) {
                                displayedDescriptions.add(desc.description);
                              }
                            });
                          }

                          return (
                            <div
                              key={`asset-${item.id || index}`}
                              className="p-2.5 border border-indigo-200 dark:border-indigo-700 rounded-md bg-indigo-100 dark:bg-indigo-900/70 shadow-sm"
                            >
                              <div className="flex items-start justify-between">
                                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                  Asset: {item.asset}
                                </p>
                              </div>
                              <div className="mt-1 border-t border-indigo-100 dark:border-indigo-800 pt-1 space-y-1">
                                {item.descriptions?.map((desc, dIndex) => (
                                  <p key={`desc-${desc.id || dIndex}`} className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Description:</span> {nl2br(desc.description)}
                                  </p>
                                ))}
                                {(!item.descriptions || item.descriptions.length === 0) && requisition.repairs && requisition.repairs[index] && !displayedDescriptions.has(requisition.repairs[index].description) && (
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    <span className="font-medium">Description:</span> {nl2br(requisition.repairs[index].description)}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        }) || [];

                        // Render legacy repairs that haven't been displayed yet
                        const legacyItems = requisition.assets?.length === 0 && requisition.repairs && requisition.repairs.length > 0
                          ? requisition.repairs.map((repair, index) => {
                              // Skip if this description was already shown
                              if (displayedDescriptions.has(repair.description)) {
                                return null;
                              }

                              return (
                                <div
                                  key={`legacy-repair-${repair.id || index}`}
                                  className="p-2.5 border border-purple-200 dark:border-purple-700 rounded-md bg-purple-100 dark:bg-purple-900/70 shadow-sm"
                                >
                                  <div className="flex items-start justify-between">
                                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                      Item {index + 1}
                                    </p>
                                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-200">
                                      Legacy
                                    </span>
                                  </div>
                                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 border-t border-purple-100 dark:border-purple-800 pt-1">
                                    <span className="font-medium">Description:</span> {nl2br(repair.description)}
                                  </p>
                                </div>
                              );
                            }).filter(Boolean as any)
                          : [];

                        items.push(...equipmentItems, ...assetItems, ...legacyItems);
                      }

                      return items;
                    })()}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400">No items listed for repair or request.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400">Failed to load requisition details</p>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md transition-colors duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickViewModal;
